import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { Button } from '../ui/button'
import { Panel, Row } from '../ui/panel'
import { Chip } from '../ui/tone'
import { ApiError, api, type TenantCredential } from '../../lib/api'
import { useSession } from '../../lib/session'

/**
 * Credentials that belong to **this customer** — P041, §54.
 *
 * ### What this replaced
 *
 * A single `CLOUDFLARE_API_TOKEN` in Axmion's own `.env`, used for every organization's DNS —
 * while `Domain.dnsZoneId` had been tenant-scoped since P027. A second customer meant either
 * holding an API token into someone else's Cloudflare account in a config file, or that customer
 * publishing every DNS record by hand forever.
 *
 * ### Nothing here can read a secret back, and the screen says so
 *
 * There is no endpoint that returns a stored value, so there is no field that could show one. What
 * an operator gets is the shape: what kind, what they called it, when it was set, and whether it
 * last failed. That is everything needed to manage a credential and nothing needed to use one.
 *
 * ### Removing is not the same as disconnecting
 *
 * The API returns what removal **falls back to**, and it is rendered, because deleting an
 * organization's Cloudflare credential does not turn automation off while Axmion still holds a
 * token — it quietly moves that organization onto Axmion's. Two different states, and only one of
 * them is usually what somebody meant.
 */

const KINDS: Array<{
  kind: TenantCredential['kind']
  title: string
  what: string
  /** What to paste, said precisely enough that nobody pastes the wrong secret. */
  hint: string
  /** Blocked on something, and on what. Rendered instead of a control. */
  blocked?: string
}> = [
  {
    kind: 'CLOUDFLARE_DNS',
    title: 'Cloudflare DNS',
    what: 'Lets Mail Room publish SPF, DKIM, DMARC and MX into your own zones (§14, §79).',
    hint: 'An API token scoped to Zone → DNS → Edit on the zones you want managed. Not a Global API Key — that one can do anything to the account including billing, and cannot be scoped or revoked on its own.',
  },
  {
    kind: 'GOOGLE_OAUTH_CLIENT',
    title: 'Your own Google app',
    what: 'Connect mailboxes through your own Google Cloud project instead of Axmion’s, so your staff see your name on the consent screen and your grants do not count against Axmion’s cap (P028, P045).',
    hint: 'The client secret from your OAuth client. The client id is not a secret and goes in the label field alongside it.',
    blocked:
      'Not wired yet — the connect flow still uses Axmion’s app. The store is ready; the flow that reads from it is not.',
  },
  {
    kind: 'MICROSOFT_OAUTH_CLIENT',
    title: 'Your own Microsoft app',
    what: 'The same, for Microsoft 365.',
    hint: 'The client secret from your app registration.',
    blocked: 'No Microsoft adapter exists yet (§116 item 13).',
  },
]

export function TenantCredentialsPanel() {
  const { can } = useSession()
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState<TenantCredential['kind'] | null>(null)
  const [secret, setSecret] = useState('')
  const [label, setLabel] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [outcome, setOutcome] = useState<string | null>(null)

  const stored = useQuery({
    queryKey: ['credentials'],
    queryFn: () => api.get<{ items: TenantCredential[] }>('/credentials'),
  })

  const done = async (message: string) => {
    setEditing(null)
    setSecret('')
    setLabel('')
    setError(null)
    setOutcome(message)
    await queryClient.invalidateQueries({ queryKey: ['credentials'] })
    await queryClient.invalidateQueries({ queryKey: ['domains'] })
  }

  const save = useMutation({
    mutationFn: (input: { kind: string; secret: string; label: string }) =>
      api.post(`/credentials/${input.kind}`, {
        secret: input.secret,
        label: input.label,
      }),
    onSuccess: () => void done('Stored. It is encrypted and cannot be read back.'),
    onError: (e: unknown) =>
      setError(e instanceof ApiError ? e.message : 'It could not be stored.'),
  })

  const remove = useMutation({
    mutationFn: (kind: string) =>
      api.del<{ removed: boolean; fallsBackTo: string }>(`/credentials/${kind}`, {
        reason: 'removed from the settings screen',
      }),
    onSuccess: (result) =>
      void done(
        result.fallsBackTo === 'platform'
          ? // The sentence that stops somebody believing they disconnected something.
            'Removed — and this organization is now using Axmion’s own token, not nothing.'
          : 'Removed. DNS automation is off for this organization; records can still be published by hand.',
      ),
    onError: (e: unknown) =>
      setError(e instanceof ApiError ? e.message : 'It could not be removed.'),
  })

  const may = can('connect_credentials')
  const byKind = new Map(
    (stored.data?.items ?? []).map((item) => [item.kind, item]),
  )

  return (
    <Panel
      title="Your credentials"
      description="Secrets that belong to this organization rather than to Axmion. Encrypted at rest, and never readable back through this console or the API (§54)."
    >
      {outcome && (
        <Row label="">
          <span className="text-muted text-[12px]">{outcome}</span>
        </Row>
      )}

      {KINDS.map((entry) => {
        const current = byKind.get(entry.kind)
        const open = editing === entry.kind

        return (
          <Row key={entry.kind} label={entry.title}>
            <div className="space-y-1.5">
              <div className="flex flex-wrap items-center gap-2">
                {current ? (
                  <>
                    <Chip tone={current.lastFailureAt ? 'bad' : 'ok'}>
                      {current.lastFailureAt ? 'not working' : 'stored'}
                    </Chip>
                    <span className="text-text">{current.label}</span>
                    <span className="text-faint text-[11.5px]">
                      set {new Date(current.updatedAt).toLocaleDateString()}
                    </span>
                  </>
                ) : entry.blocked ? (
                  <Chip tone="neutral">not available yet</Chip>
                ) : (
                  <Chip tone="neutral">using Axmion’s</Chip>
                )}
              </div>

              <p className="text-faint m-0 text-[11.5px]">{entry.what}</p>

              {current?.lastFailureDetail && (
                <p className="text-bad m-0 text-[11.5px]">
                  Last failure: {current.lastFailureDetail}
                </p>
              )}

              {entry.blocked ? (
                <p className="text-muted m-0 text-[11.5px]">{entry.blocked}</p>
              ) : may && open ? (
                <div className="space-y-2">
                  <input
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    placeholder="What to call it"
                    aria-label={`Label for ${entry.title}`}
                    className="bg-panel2 border-line focus:border-accent w-full rounded-[7px] border px-2 py-1 text-[12.5px] transition-colors focus:outline-none"
                  />
                  <input
                    value={secret}
                    onChange={(e) => setSecret(e.target.value)}
                    // A password field, because this is read aloud from a screen far more often
                    // than it is typed, and the value cannot be recovered from anywhere else.
                    type="password"
                    placeholder="Paste the secret"
                    aria-label={`Secret for ${entry.title}`}
                    className="bg-panel2 border-line focus:border-accent w-full rounded-[7px] border px-2 py-1 font-mono text-[12px] transition-colors focus:outline-none"
                  />
                  <p className="text-faint m-0 text-[11.5px]">{entry.hint}</p>
                  {error && <p className="text-bad m-0 text-[11.5px]">{error}</p>}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="primary"
                      disabled={
                        save.isPending ||
                        secret.trim().length < 8 ||
                        label.trim().length < 2
                      }
                      onClick={() =>
                        save.mutate({
                          kind: entry.kind,
                          secret: secret.trim(),
                          label: label.trim(),
                        })
                      }
                    >
                      Store it
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => {
                        setEditing(null)
                        setSecret('')
                        setError(null)
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : may ? (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => {
                      setEditing(entry.kind)
                      setLabel(current?.label ?? '')
                      setSecret('')
                      setOutcome(null)
                    }}
                  >
                    {current ? 'Replace' : 'Add yours'}
                  </Button>
                  {current && (
                    <Button
                      size="sm"
                      variant="danger"
                      disabled={remove.isPending}
                      onClick={() => remove.mutate(entry.kind)}
                    >
                      Remove
                    </Button>
                  )}
                </div>
              ) : null}
            </div>
          </Row>
        )
      })}

      {!may && (
        <Row label="Your access">
          <span className="text-muted text-[12px]">
            Storing one needs <code>connect_credentials</code>. Seeing that they exist does not.
          </span>
        </Row>
      )}
    </Panel>
  )
}
